import { useEffect, useMemo, useState } from "react";
import { useLocation, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useMyMenus } from "@/hooks/useMyMenus";
import { api, fmtDate, inr } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sparkles, FileText, Plus, Link as LinkIcon, Copy, Download, Settings as Cog,
  Layers, Wallet, TrendingUp, History, Trash2, Search, LayoutGrid, List as ListIcon, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SearchablePicker, { leadMatch, renderLeadRow } from "@/components/app/SearchablePicker";
import Pagination from "@/components/app/Pagination";

export default function Proposals() {
  const { user } = useAuth();
  const { menus } = useMyMenus(user?.role);
  const canManagePresets = !!menus?.has("proposal-settings");
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const [proposals, setProposals] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [createOpen, setCreateOpen] = useState(Boolean(state?.leadId));
  // If we arrived here from a "N proposals →" link, scope the list to that
  // lead's proposals instead of triggering the create wizard.
  const [leadFilter, setLeadFilter] = useState(
    state?.filterLeadId ? { id: state.filterLeadId, name: state.leadName || "" } : null
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState("grid");
  const [statusFilter, setStatusFilter] = useState("all");
  // Server-side pagination: /proposals-v2?paged=1 returns {items,total} with
  // search/status/lead filters applied server-side.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 400);
    return () => clearTimeout(t);
  }, [q]);

  // Deep-link create: /proposals with state.leadId (lead detail quick action)
  // mounts the dialog already open — onOpenChange never fires, so load the
  // template options here.
  useEffect(() => { if (createOpen) openCreateWizard(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const load = async () => {
    const params = new URLSearchParams({
      paged: "1", limit: String(pageSize), skip: String((page - 1) * pageSize),
    });
    if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (leadFilter?.id) params.set("lead_id", leadFilter.id);
    const p = await api.get(`/proposals-v2?${params}`);
    setProposals(p.data.items || []);
    setTotal(p.data.total || 0);
  };

  // Slim template options load lazily — only when the create wizard or the
  // edit drawer actually opens, never on page load.
  const loadTemplateOptions = async () => {
    const r = await api.get("/system-templates/options");
    setTemplates(r.data || []);
    return r.data || [];
  };

  const openCreateWizard = async () => {
    // Seed defaults on first use when the catalogue is empty, then load the
    // slim options list for the pickers.
    const opts = await loadTemplateOptions();
    if (!opts.length) {
      try {
        const s = await api.post("/system-templates/seed");
        if (s.data?.seeded) toast.success(`Loaded ${s.data.seeded} templates`);
        await loadTemplateOptions();
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to seed"); }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, pageSize, debouncedQ, statusFilter, leadFilter]);
  useEffect(() => { setPage(1); }, [debouncedQ, statusFilter, leadFilter, view, pageSize]);

  // Deep-link: /proposals?open=<id> (e.g. "Open proposal" from Project Detail,
  // which opens in a new tab so location.state is unavailable) auto-opens the
  // proposal detail dialog. The drawer fetches the proposal itself, so it works
  // even when the record isn't on the current page.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && openId !== activeId) setActiveId(openId);
    // eslint-disable-next-line
  }, [searchParams]);

  // Edit-drawer Selects need the slim template options — fetch them only when
  // a drawer actually opens, never on page load.
  useEffect(() => { if (activeId) loadTemplateOptions(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeId]);

  return (
    <div data-testid="proposals-page">
      <PageHeader
        eyebrow="Design & Pricing"
        title="Solar Proposals"
        subtitle="Pick predefined system templates, compare two options side-by-side, generate AI copy, and ship a 4-page branded PDF in minutes."
        action={
          <div className="flex flex-wrap gap-2">
            {canManagePresets && (
              <Button onClick={() => setSettingsOpen(true)} variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-11 px-4" data-testid="proposal-settings-btn">
                <Cog className="w-4 h-4 mr-2" /> Settings
              </Button>
            )}
            <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (o) { openCreateWizard(); } }}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="new-proposal-button">
                  <Plus className="w-4 h-4 mr-2" /> New Proposal
                </Button>
              </DialogTrigger>
              {/* Mount only while open so the wizard's lead-options fetch
                  fires on demand, not on page load. */}
              {createOpen && (
                <CreateProposalWizard
                  templates={templates}
                  presetLeadId={state?.leadId}
                  onDone={(id) => { setCreateOpen(false); setActiveId(id); load(); }}
                />
              )}
            </Dialog>
          </div>
        }
      />

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

      <div className="px-4 sm:px-8 py-6 space-y-4" data-testid="proposals-list">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by customer, template, version, status…"
              className="rounded-none h-10 pl-9"
              data-testid="proposals-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-none h-10 w-full lg:w-56" data-testid="proposals-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-xs text-[#4B5563]">
            Showing <b>{proposals.length}</b> of <b>{total}</b>
          </div>
        </div>

        {leadFilter && (
          <div
            className="flex items-center gap-2 bg-[#EFF6FF] border border-[#002FA7] px-3 py-2 text-xs"
            data-testid="proposals-lead-filter-chip"
          >
            <span className="label-eyebrow !text-[10px] text-[#002FA7]">Filtered by lead</span>
            <b className="text-[#0a0a0a]">{leadFilter.name || leadFilter.id}</b>
            <button
              onClick={() => setLeadFilter(null)}
              className="ml-auto text-[10px] uppercase tracking-widest font-bold text-[#DC2626] hover:underline"
              data-testid="proposals-clear-lead-filter"
            >
              Clear ✕
            </button>
          </div>
        )}

        <Tabs value={view} onValueChange={setView} data-testid="proposals-view-tabs">
          <TabsList className="rounded-none bg-[#F3F4F6] p-1 h-auto">
            <TabsTrigger value="grid" className="rounded-none text-xs uppercase tracking-widest font-bold" data-testid="proposals-view-grid">
              <LayoutGrid className="w-3 h-3 mr-1.5" /> Grid View
            </TabsTrigger>
            <TabsTrigger value="list" className="rounded-none text-xs uppercase tracking-widest font-bold" data-testid="proposals-view-list">
              <ListIcon className="w-3 h-3 mr-1.5" /> List View
            </TabsTrigger>
          </TabsList>

          <TabsContent value="grid" className="mt-4">
            {proposals.length === 0 ? (
              <div className="border border-dashed border-[#E5E7EB] text-center py-12 text-sm text-[#9CA3AF]">
                {total === 0 && !debouncedQ && statusFilter === "all" && !leadFilter ? `No proposals yet. Click "New Proposal" to start.` : "No proposals match your search."}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {proposals.map((p) => (
                  <ProposalCard key={p.id} prop={p} onOpen={() => setActiveId(p.id)} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="list" className="mt-4">
            <ProposalListView proposals={proposals} onOpen={setActiveId} />
          </TabsContent>
        </Tabs>

        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testidPrefix="proposals-pagination"
        />
      </div>

      <Dialog open={!!activeId} onOpenChange={(o) => !o && setActiveId(null)}>
        {activeId && (
          <ProposalDrawer
            id={activeId}
            templates={templates}
            onChange={load}
            onClose={() => setActiveId(null)}
          />
        )}
      </Dialog>
    </div>
  );
}

function ProposalCard({ prop, onOpen }) {
  const r1 = prop.option1?.roi;
  const r2 = prop.option2?.roi;
  return (
    <button onClick={onOpen} className="text-left border border-[#E5E7EB] bg-white p-4 hover:border-[#0a0a0a] transition-colors" data-testid={`proposal-card-${prop.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="font-black text-lg tracking-tight truncate">{prop.customer?.name || "Customer"}</div>
          <div className="text-xs text-[#4B5563]">v{prop.version} · {prop.created_by} · {fmtDate(prop.created_at)}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 ${prop.status === "sent" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#F3F4F6] text-[#4B5563]"}`}>{prop.status}</span>
          {prop.public_token && <LinkIcon className="w-3 h-3 text-[#10B981]" />}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <OptSummary label="Option 1" tmpl={prop.option1?.template} roi={r1} />
        <OptSummary label="Option 2" tmpl={prop.option2?.template} roi={r2} />
      </div>
    </button>
  );
}
function OptSummary({ label, tmpl, roi }) {
  if (!tmpl) return <div className="border border-dashed border-[#E5E7EB] p-2 text-xs text-[#9CA3AF]">{label}: —</div>;
  return (
    <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-2">
      <div className="label-eyebrow !text-[9px]">{label}</div>
      <div className="font-bold text-sm truncate">{tmpl.name}</div>
      <div className="text-[11px] text-[#4B5563] mt-1">Final: <b>{inr(roi?.final_price_inr || 0)}</b></div>
      <div className="text-[11px] text-[#4B5563]">Payback: <b>{roi?.payback_years || "—"} yrs</b></div>
    </div>
  );
}

function ProposalListView({ proposals, onOpen }) {
  if (proposals.length === 0) {
    return <div className="border border-dashed border-[#E5E7EB] text-center py-12 text-sm text-[#9CA3AF]">No proposals match your search.</div>;
  }
  return (
    <div className="border border-[#E5E7EB] overflow-x-auto" data-testid="proposals-list-table">
      <table className="w-full text-sm">
        <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
          <tr>
            {["Customer", "v#", "Option 1", "Final 1", "Option 2", "Final 2", "Status", "Created", ""].map((h, i) => (
              <th key={i} className="text-left label-eyebrow !text-[10px] px-3 py-3">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {proposals.map((p) => (
            <tr key={p.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer"
                onClick={() => onOpen(p.id)} data-testid={`proposal-list-row-${p.id}`}>
              <td className="px-3 py-2">
                <div className="font-semibold">{p.customer?.name || "—"}</div>
                <div className="text-[10px] text-[#4B5563]">{p.customer?.phone || ""}</div>
              </td>
              <td className="px-3 py-2 text-xs font-mono">v{p.version}</td>
              <td className="px-3 py-2 text-xs">{p.option1?.template?.name || "—"}</td>
              <td className="px-3 py-2 text-xs font-bold">{p.option1?.roi?.final_price_inr ? inr(p.option1.roi.final_price_inr) : "—"}</td>
              <td className="px-3 py-2 text-xs">{p.option2?.template?.name || <span className="text-[#9CA3AF]">—</span>}</td>
              <td className="px-3 py-2 text-xs font-bold">{p.option2?.roi?.final_price_inr ? inr(p.option2.roi.final_price_inr) : <span className="text-[#9CA3AF]">—</span>}</td>
              <td className="px-3 py-2">
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${p.status === "sent" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#F3F4F6] text-[#4B5563]"}`}>
                  {p.status}
                </span>
                {p.public_token && <LinkIcon className="w-3 h-3 inline-block ml-1 text-[#10B981]" />}
              </td>
              <td className="px-3 py-2 text-xs">{fmtDate(p.created_at)}</td>
              <td className="px-3 py-2 text-right">
                <span className="text-[10px] font-bold text-[#002FA7] uppercase tracking-widest">Open →</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =====================================================================
//  CREATE WIZARD — step lead → step options → confirm
// =====================================================================
function CreateProposalWizard({ templates, presetLeadId, onDone }) {
  const [leadId, setLeadId] = useState(presetLeadId || "");
  // Selected lead object resolved by the picker (optionsEndpoint mode) — used
  // for the billing-cycle preview.
  const [selectedLead, setSelectedLead] = useState(null);
  const [opt1, setOpt1] = useState({ template_id: "", discount_amount: 0, discount_type: "fixed", extra_items: [] });
  const [opt2, setOpt2] = useState({ template_id: "", discount_amount: 0, discount_type: "fixed", extra_items: [] });
  const [enableOpt2, setEnableOpt2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState("");
  const [tokenAdvance, setTokenAdvance] = useState(10000);
  const [showPrices, setShowPrices] = useState(false);
  useEffect(() => {
    api.get("/proposal-setting-presets").then((r) => {
      const list = r.data || [];
      setPresets(list);
      const def = list.find((p) => p.is_default) || list[0];
      if (def) setPresetId(def.id);
    }).catch(() => setPresets([]));
  }, []);

  const lead = selectedLead && selectedLead.id === leadId ? selectedLead : null;

  const submit = async () => {
    if (!leadId) { toast.error("Pick a lead"); return; }
    if (!opt1.template_id) { toast.error("Pick template for Option 1"); return; }
    setBusy(true);
    try {
      const payload = {
        lead_id: leadId,
        settings_preset_id: presetId || null,
        token_advance_inr: Number(tokenAdvance) || 0,
        show_prices: showPrices,
        option1: {
          ...opt1,
          discount_amount: Number(opt1.discount_amount) || 0,
          items_override: cleanOverride(opt1.items_override),
          extra_items: (opt1.extra_items || [])
            .filter((e) => e.name?.trim())
            .map((e) => ({ ...e, price_inr: Number(e.price_inr) || 0 })),
        },
      };
      if (enableOpt2 && opt2.template_id) {
        payload.option2 = {
          ...opt2,
          discount_amount: Number(opt2.discount_amount) || 0,
          items_override: cleanOverride(opt2.items_override),
          extra_items: (opt2.extra_items || [])
            .filter((e) => e.name?.trim())
            .map((e) => ({ ...e, price_inr: Number(e.price_inr) || 0 })),
        };
      }
      const r = await api.post("/proposals-v2", payload);
      toast.success(`Proposal v${r.data.version} created`);
      onDone(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="proposal-wizard">
      <DialogHeader>
        <DialogTitle className="font-black text-2xl tracking-tight">New Proposal</DialogTitle>
      </DialogHeader>

      <div className="space-y-5 pt-2">
        {/* STEP 1 - LEAD */}
        <Section icon={Layers} title="1. Select Lead">
          <SearchablePicker
            items={[]}
            value={leadId}
            onChange={setLeadId}
            optionsEndpoint="/leads/options"
            onSelectItem={setSelectedLead}
            renderRow={renderLeadRow}
            selectedLabel={(l) => `${l.name || "(no name)"} · ${l.phone || "no phone"}`}
            placeholder="Search by name, phone or district…"
            testid="proposal-lead-select"
            emptyHint="No leads match your search."
          />
          {lead && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs" data-testid="lead-cycle-preview">
              <Info label="Billing Cycle" value={lead.billing_cycle || "Monthly"} />
              <Info label={`${lead.billing_cycle === "Bi-Monthly" ? "Bi-Monthly" : "Monthly"} Consumption`}
                    value={`${lead.billing_cycle_consumption_kwh || lead.monthly_consumption_kwh || 0} kWh`} />
              <Info label={`${lead.billing_cycle === "Bi-Monthly" ? "Bi-Monthly" : "Monthly"} Bill`}
                    value={inr(lead.billing_cycle_bill_inr || lead.monthly_bill_inr || 0)} />
              <Info label="Equiv. Monthly Bill" value={inr(lead.monthly_bill_inr || 0)} />
            </div>
          )}
        </Section>

        {/* STEP 1.5 - SETTINGS PRESET */}
        <Section icon={Cog} title="2. Settings Preset">
          <Select value={presetId} onValueChange={setPresetId}>
            <SelectTrigger className="rounded-none h-10" data-testid="proposal-preset-select">
              <SelectValue placeholder="Pick a preset" />
            </SelectTrigger>
            <SelectContent>
              {presets.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`proposal-preset-${p.id}`}>
                  {p.name}{p.is_default ? " · default" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-[10px] text-[#9CA3AF] mt-2">
            Controls warranty, T&amp;C, payment terms and bank details on the generated PDF.
            The chosen preset is snapshotted onto the proposal — future preset edits won't change this PDF.
          </div>

          <div className="mt-4 pt-4 border-t border-dashed border-[#E5E7EB]">
            <Label className="label-eyebrow">Token Advance Amount (₹)</Label>
            <Input
              type="number"
              value={tokenAdvance}
              onChange={(e) => setTokenAdvance(e.target.value)}
              className="rounded-none mt-2 h-10 max-w-xs"
              placeholder="10000"
              data-testid="proposal-token-advance"
            />
            <div className="text-[10px] text-[#9CA3AF] mt-1">
              Pre-fills the UPI deep-link on the customer&apos;s shareable proposal link and prints next to the QR on the PDF.
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-dashed border-[#E5E7EB]">
            <Label className="label-eyebrow">Line-item Pricing</Label>
            <div className="mt-2 flex gap-6" role="radiogroup">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="proposal-with-price">
                <input
                  type="radio"
                  name="show_prices"
                  checked={!showPrices}
                  onChange={() => setShowPrices(false)}
                  className="accent-[#002FA7]"
                />
                <span className="text-sm font-bold">Without price</span>
                <span className="text-[10px] text-[#9CA3AF]">(default · lump-sum quote)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="proposal-without-price">
                <input
                  type="radio"
                  name="show_prices"
                  checked={showPrices}
                  onChange={() => setShowPrices(true)}
                  className="accent-[#002FA7]"
                />
                <span className="text-sm font-bold">With price</span>
                <span className="text-[10px] text-[#9CA3AF]">(itemised pricing per row)</span>
              </label>
            </div>
            <div className="text-[10px] text-[#9CA3AF] mt-1">
              When enabled, the PDF&apos;s System Components table includes a Price column populated from each template item&apos;s saved price.
            </div>
          </div>
        </Section>

        {/* STEP 2 - OPTION 1 */}
        <Section icon={Wallet} title="3. Option 1">
          <OptionPicker option={opt1} setOption={setOpt1} templates={templates} showPrices={showPrices} testidBase="opt1" />
        </Section>

        {/* STEP 2.5 - OPTION 2 */}
        <Section icon={TrendingUp} title="4. Option 2 (for comparison)" right={
          <label className="text-xs flex items-center gap-2" data-testid="enable-opt2-toggle">
            <input type="checkbox" checked={enableOpt2} onChange={(e) => setEnableOpt2(e.target.checked)} className="w-4 h-4" />
            <span>Enable Option 2</span>
          </label>
        }>
          {enableOpt2 ? (
            <OptionPicker option={opt2} setOption={setOpt2} templates={templates} showPrices={showPrices} testidBase="opt2" />
          ) : (
            <div className="text-xs text-[#9CA3AF]">Tick "Enable Option 2" to add a comparison.</div>
          )}
        </Section>

        <Button onClick={submit} disabled={busy} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-12 uppercase tracking-widest text-xs font-bold" data-testid="proposal-create-submit">
          {busy ? "Creating…" : "Create Proposal"}
        </Button>
      </div>
    </DialogContent>
  );
}
function Section({ icon: Icon, title, right, children }) {
  return (
    <div className="border border-[#E5E7EB] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#002FA7]" />
          <div className="font-bold text-sm tracking-tight">{title}</div>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}
function Info({ label, value }) {
  return <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-2"><div className="label-eyebrow !text-[9px]">{label}</div><div className="font-bold text-sm">{value}</div></div>;
}

// ---------------------------------------------------------------------------
//  Line-items editing — per-proposal override of the template BOM, saved as
//  option.items_override. The backend swaps it in for ROI math, PDF rendering
//  and invoice pre-fill. Editable until the proposal is confirmed (admin can
//  still edit afterwards).
// ---------------------------------------------------------------------------
const qtyFromItem = (it) => {
  const qn = Number(it.qty_num);
  const qs = String(it.quantity || "").trim();
  if (qn && qn !== 1) return qn;
  const parsed = parseFloat(qs);
  if (qs && qs !== "1" && !isNaN(parsed)) return parsed;
  return qn || 1;
};
const toEditableItems = (items) => (items || []).map((it) => ({
  name: it.name || "",
  description: it.description || "",
  qty_num: qtyFromItem(it),
  quantity: String(qtyFromItem(it)),
  price_inr: Number(it.price_inr || 0),
  cgst_pct: Number(it.cgst_pct || 0),
  sgst_pct: Number(it.sgst_pct || 0),
  igst_pct: Number(it.igst_pct || 0),
}));
const lineItemsTotal = (items) => (items || []).reduce((s, it) => s + (Number(it.qty_num) || 0) * (Number(it.price_inr) || 0), 0);
const cleanOverride = (items) => {
  const cleaned = (items || [])
    .filter((e) => (e.name || "").trim())
    .map((e) => ({ ...e, qty_num: Number(e.qty_num) || 1, quantity: String(Number(e.qty_num) || 1), price_inr: Number(e.price_inr) || 0 }));
  return cleaned.length ? cleaned : null;
};

function LineItemsEditor({ items, setItems, testidBase }) {
  const upd = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const del = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () => setItems([...items, { name: "", description: "", qty_num: 1, quantity: "1", price_inr: 0, cgst_pct: 0, sgst_pct: 0, igst_pct: 0 }]);
  return (
    <div data-testid={`${testidBase}-items-editor`}>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[#E5E7EB]">
              <th className="text-left label-eyebrow !text-[9px] px-1 py-2">Item</th>
              <th className="text-left label-eyebrow !text-[9px] px-1 py-2">Description</th>
              <th className="text-right label-eyebrow !text-[9px] px-1 py-2 w-16">Qty</th>
              <th className="text-right label-eyebrow !text-[9px] px-1 py-2 w-24">Rate (₹)</th>
              <th className="text-right label-eyebrow !text-[9px] px-1 py-2 w-24">Line Total</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const lineTotal = (Number(it.qty_num) || 0) * (Number(it.price_inr) || 0);
              return (
                <tr key={i} className="border-b border-[#F3F4F6]" data-testid={`${testidBase}-item-row-${i}`}>
                  <td className="px-1 py-1"><Input value={it.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Item" className="rounded-none h-8 text-xs min-w-[110px]" data-testid={`${testidBase}-item-name-${i}`} /></td>
                  <td className="px-1 py-1"><Input value={it.description || ""} onChange={(e) => upd(i, { description: e.target.value })} placeholder="Description" className="rounded-none h-8 text-xs min-w-[130px]" data-testid={`${testidBase}-item-desc-${i}`} /></td>
                  <td className="px-1 py-1"><Input type="number" min="0" step="any" value={it.qty_num} onChange={(e) => upd(i, { qty_num: e.target.value, quantity: e.target.value })} className="rounded-none h-8 text-xs text-right" data-testid={`${testidBase}-item-qty-${i}`} /></td>
                  <td className="px-1 py-1"><Input type="number" min="0" step="any" value={it.price_inr} onChange={(e) => upd(i, { price_inr: e.target.value })} className="rounded-none h-8 text-xs text-right" data-testid={`${testidBase}-item-rate-${i}`} /></td>
                  <td className="px-2 py-1 text-right font-mono font-bold whitespace-nowrap" data-testid={`${testidBase}-item-total-${i}`}>{lineTotal > 0 ? inr(lineTotal) : "—"}</td>
                  <td className="px-1 py-1 text-right">
                    <button type="button" onClick={() => del(i)} className="p-1.5 text-[#9CA3AF] hover:text-[#DC2626] hover:bg-[#FEF2F2]" title="Remove line" data-testid={`${testidBase}-item-del-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-2">
        <button type="button" onClick={add} className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline inline-flex items-center gap-1" data-testid={`${testidBase}-item-add`}>
          <Plus className="w-3 h-3" /> Add Row
        </button>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563]" data-testid={`${testidBase}-items-total`}>
          Total {inr(lineItemsTotal(items))}
        </div>
      </div>
    </div>
  );
}

// Drawer BOM section: read-only list by default; "Edit Items" switches to the
// editable grid and persists via onSaveItems (null resets to template defaults).
function OptionItemsSection({ option, canEdit, onSaveItems, testidBase }) {
  const items = option.template?.items || [];
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const customized = Array.isArray(option.items_override) && option.items_override.length > 0;
  const save = async (val) => {
    setSaving(true);
    try { await onSaveItems(val); setDraft(null); } finally { setSaving(false); }
  };
  const saveDraft = () => {
    const cleaned = cleanOverride(draft);
    save(cleaned);
  };
  return (
    <div className="border-t border-[#F3F4F6] pt-2 text-xs" data-testid={`${testidBase}-items-section`}>
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow !text-[9px]">
          Bill of Materials ({items.length} items)
          {customized && <span className="text-[#B45309]" data-testid={`${testidBase}-items-customized-badge`}> · customized</span>}
        </div>
        {canEdit && draft === null && (
          <button type="button" onClick={() => setDraft(toEditableItems(items))}
            className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline inline-flex items-center gap-1"
            data-testid={`${testidBase}-items-edit-btn`}>
            <Pencil className="w-3 h-3" /> Edit Items
          </button>
        )}
      </div>
      {draft === null ? (
        <div className="space-y-1">
          {items.map((it, idx) => (
            <div key={idx} className="border border-[#F3F4F6] p-2" data-testid={`${testidBase}-bom-row-${idx}`}>
              <div className="font-semibold">{it.name} <span className="text-[#9CA3AF]">× {qtyFromItem(it)}</span></div>
              <div className="text-[10px] text-[#4B5563]">{it.description}</div>
            </div>
          ))}
        </div>
      ) : (
        <div>
          <LineItemsEditor items={draft} setItems={setDraft} testidBase={`${testidBase}-edit`} />
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" disabled={saving} onClick={saveDraft} className="rounded-none bg-[#0a0a0a] hover:bg-black uppercase tracking-widest text-[10px] font-bold h-8" data-testid={`${testidBase}-items-save`}>
              {saving ? "Saving…" : "Save Items"}
            </Button>
            <Button size="sm" variant="outline" disabled={saving} onClick={() => setDraft(null)} className="rounded-none uppercase tracking-widest text-[10px] font-bold h-8" data-testid={`${testidBase}-items-cancel`}>Cancel</Button>
            {customized && (
              <button type="button" disabled={saving} onClick={() => save(null)} className="text-[10px] uppercase tracking-widest font-bold text-[#DC2626] hover:underline ml-auto" data-testid={`${testidBase}-items-reset`}>
                Reset to Template
              </button>
            )}
          </div>
          <div className="text-[10px] text-[#9CA3AF] mt-2">Editable until the proposal is confirmed. Line Total = Qty × Rate.</div>
        </div>
      )}
    </div>
  );
}

function OptionPicker({ option, setOption, templates, showPrices, testidBase }) {
  // Picker rows come from the slim /system-templates/options list; the full
  // template (BOM items) is fetched only when the user actually picks one.
  const [tmplFull, setTmplFull] = useState(null);
  useEffect(() => {
    setTmplFull(null);
    if (option.template_id) {
      api.get(`/system-templates/${option.template_id}`).then((r) => setTmplFull(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option.template_id]);
  const tmpl = templates.find((t) => t.id === option.template_id);
  const previewTmpl = tmplFull || tmpl;
  // Per-deal edited copy of the template BOM (sent as items_override).
  const override = Array.isArray(option.items_override) ? option.items_override : null;
  const template_price = override ? lineItemsTotal(override) : Number(tmpl?.package_price_inr || 0);
  const extras = option.extra_items || [];
  const extras_total = extras.reduce((s, e) => s + (Number(e.price_inr) || 0), 0);
  const package_price = template_price + extras_total;
  const discountInr = option.discount_type === "percentage"
    ? Math.round(package_price * (Number(option.discount_amount) || 0) / 100)
    : (Number(option.discount_amount) || 0);
  const finalPrice = Math.max(0, package_price - discountInr);

  const addExtra = () => setOption({ ...option, extra_items: [...extras, { name: "", description: "", quantity: "1", price_inr: 0 }] });
  const updExtra = (i, patch) => setOption({
    ...option,
    extra_items: extras.map((e, idx) => idx === i ? { ...e, ...patch } : e),
  });
  const delExtra = (i) => setOption({ ...option, extra_items: extras.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-3">
      {/* Searchable template picker — free-text substring match on template
          name, system type and package type; row shows package price + item
          count on the second line. */}
      <SearchablePicker
        items={templates}
        value={option.template_id}
        onChange={(v) => setOption({ ...option, template_id: v, items_override: null })}
        placeholder="Search a template…"
        testid={`${testidBase}-template`}
        selectedLabel={(t) => `${t.name} · ${inr(t.package_price_inr)}`}
        matchItem={(t, s) => (
          (t.name || "").toLowerCase().includes(s) ||
          (t.system_type || "").toLowerCase().includes(s) ||
          (t.package_type || "").toLowerCase().includes(s)
        )}
        renderRow={(t) => (
          <>
            <div className="text-sm font-semibold truncate">{t.name}</div>
            <div className="text-[11px] text-[#4B5563] truncate">
              {t.system_type} · {t.package_type} · {inr(t.package_price_inr)}
              {(t.items_count ?? (t.items || []).length) ? ` · ${t.items_count ?? (t.items || []).length} items` : ""}
            </div>
          </>
        )}
      />

      {/* Template preview — every line item from the picked template so the
          proposal creator can see the bill of materials at a glance. Prices +
          line totals only render when the proposal is marked as "with price"
          (per user request to assist proposal creation). The full template is
          fetched on demand via GET /system-templates/{id}. */}
      {previewTmpl && ((previewTmpl.items || []).length > 0 || override) && (
        <div className="border border-[#E5E7EB]" data-testid={`${testidBase}-template-preview`}>
          <div className="px-3 py-2 border-b border-[#E5E7EB] bg-[#F9FAFB] flex items-center justify-between gap-2 flex-wrap">
            <div className="label-eyebrow">
              Template Line Items · {(override || previewTmpl.items || []).length}
              {override && <span className="text-[#B45309]" data-testid={`${testidBase}-items-customized-badge`}> · customized</span>}
            </div>
            <div className="flex items-center gap-3">
              {showPrices && !override && (
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563]">
                  Total {inr((previewTmpl.items || []).reduce((s, it) => s + (Number(it.qty_num || 1) * Number(it.price_inr || 0)), 0))}
                </div>
              )}
              {override ? (
                <button type="button" onClick={() => setOption({ ...option, items_override: null })}
                  className="text-[10px] uppercase tracking-widest font-bold text-[#DC2626] hover:underline"
                  data-testid={`${testidBase}-items-reset`}>Reset to Template</button>
              ) : (
                <button type="button" onClick={() => setOption({ ...option, items_override: toEditableItems(previewTmpl.items) })}
                  className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline inline-flex items-center gap-1"
                  data-testid={`${testidBase}-items-edit`}>
                  <Pencil className="w-3 h-3" /> Edit Items
                </button>
              )}
            </div>
          </div>
          {override ? (
            <div className="p-2">
              <LineItemsEditor items={override} setItems={(v) => setOption({ ...option, items_override: v })} testidBase={`${testidBase}-tpl`} />
            </div>
          ) : (
          <table className="w-full text-xs">
            <thead className="bg-white border-b border-[#F3F4F6]">
              <tr>
                <th className="text-left label-eyebrow !text-[9px] px-3 py-2">Item</th>
                <th className="text-left label-eyebrow !text-[9px] px-3 py-2">Description</th>
                <th className="text-right label-eyebrow !text-[9px] px-3 py-2">Qty</th>
                {showPrices && <th className="text-right label-eyebrow !text-[9px] px-3 py-2">Rate</th>}
                {showPrices && <th className="text-right label-eyebrow !text-[9px] px-3 py-2">GST %</th>}
                {showPrices && <th className="text-right label-eyebrow !text-[9px] px-3 py-2">Line Total</th>}
              </tr>
            </thead>
            <tbody>
              {previewTmpl.items.map((it, i) => {
                // Prefer numeric qty_num when explicitly set (≠ 1). Otherwise
                // parse the legacy string `quantity` ("5", "1 Set") for the
                // multiplier, and use the full string for display.
                const qtyNumRaw = Number(it.qty_num);
                const qtyStr = String(it.quantity || "").trim();
                const parsedStr = parseFloat(qtyStr);
                const qty = (qtyNumRaw && qtyNumRaw !== 1) ? qtyNumRaw
                  : (qtyStr && qtyStr !== "1" && !isNaN(parsedStr)) ? parsedStr
                  : (qtyNumRaw || 1);
                const qtyLabel = (qtyNumRaw && qtyNumRaw !== 1)
                  ? (qty === Math.floor(qty) ? qty : qty)
                  : (qtyStr && qtyStr !== "1" ? qtyStr : (qty === Math.floor(qty) ? qty : qty));
                const price = Number(it.price_inr || 0);
                const gstPct = Number(it.cgst_pct || 0) + Number(it.sgst_pct || 0) + Number(it.igst_pct || 0);
                return (
                  <tr key={i} className="border-b border-[#F3F4F6] last:border-0" data-testid={`${testidBase}-tpl-item-${i}`}>
                    <td className="px-3 py-1.5 font-semibold">{it.name}</td>
                    <td className="px-3 py-1.5 text-[#4B5563]">{it.description || "—"}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{qtyLabel}</td>
                    {showPrices && <td className="px-3 py-1.5 text-right font-mono">{price > 0 ? inr(price) : "—"}</td>}
                    {showPrices && <td className="px-3 py-1.5 text-right font-mono">{gstPct > 0 ? `${gstPct}%` : "—"}</td>}
                    {showPrices && <td className="px-3 py-1.5 text-right font-black">{price > 0 ? inr(qty * price) : "—"}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      )}

      {/* Extra custom line items */}
      <div className="border border-dashed border-[#E5E7EB] p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="label-eyebrow">Custom Line Items <span className="text-[#9CA3AF] normal-case">(added on top of the template)</span></div>
          <button
            type="button"
            onClick={addExtra}
            className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline inline-flex items-center gap-1"
            data-testid={`${testidBase}-add-extra`}
          >
            <Plus className="w-3 h-3" /> Add Item
          </button>
        </div>
        {extras.length === 0 ? (
          <div className="text-[11px] text-[#9CA3AF]">No custom items. Click &quot;Add Item&quot; to include net meter, extra panels, civil work, etc.</div>
        ) : (
          <div className="space-y-2">
            {extras.map((e, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`${testidBase}-extra-row-${i}`}>
                <Input value={e.name} onChange={(ev) => updExtra(i, { name: ev.target.value })} placeholder="Item name" className="rounded-none h-9 col-span-3" data-testid={`${testidBase}-extra-name-${i}`} />
                <Input value={e.description || ""} onChange={(ev) => updExtra(i, { description: ev.target.value })} placeholder="Description (optional)" className="rounded-none h-9 col-span-5" data-testid={`${testidBase}-extra-desc-${i}`} />
                <Input value={e.quantity || ""} onChange={(ev) => updExtra(i, { quantity: ev.target.value })} placeholder="Qty" className="rounded-none h-9 col-span-1" data-testid={`${testidBase}-extra-qty-${i}`} />
                <Input type="number" value={e.price_inr || ""} onChange={(ev) => updExtra(i, { price_inr: ev.target.value })} placeholder="Price ₹" className="rounded-none h-9 col-span-2" data-testid={`${testidBase}-extra-price-${i}`} />
                <button
                  type="button"
                  onClick={() => delExtra(i)}
                  className="col-span-1 inline-flex items-center justify-center w-9 h-9 text-[#9CA3AF] hover:text-[#DC2626] hover:bg-[#FEF2F2] border border-transparent hover:border-[#FECACA]"
                  data-testid={`${testidBase}-extra-del-${i}`}
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="label-eyebrow">Discount Type</Label>
          <Select value={option.discount_type} onValueChange={(v) => setOption({ ...option, discount_type: v })}>
            <SelectTrigger className="rounded-none mt-2 h-10" data-testid={`${testidBase}-discount-type`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
              <SelectItem value="percentage">Percentage (%)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-eyebrow">Discount</Label>
          <Input type="number" value={option.discount_amount} onChange={(e) => setOption({ ...option, discount_amount: e.target.value })} className="rounded-none mt-2 h-10" data-testid={`${testidBase}-discount-amount`} />
        </div>
      </div>
      {tmpl && (
        <div className="grid grid-cols-4 gap-2 text-xs">
          <Info label="Template" value={inr(template_price)} />
          <Info label="Extras" value={inr(extras_total)} />
          <Info label="Discount" value={`− ${inr(discountInr)}`} />
          <Info label="Final" value={inr(finalPrice)} />
        </div>
      )}
    </div>
  );
}

// =====================================================================
//  PROPOSAL DRAWER (edit options · AI · share · PDF)
// =====================================================================
function ProposalDrawer({ id, templates, onChange, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [prop, setProp] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmDlgOpen, setConfirmDlgOpen] = useState(false);
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const reload = async () => {
    const r = await api.get(`/proposals-v2/${id}`);
    setProp(r.data);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  if (!prop) return <DialogContent className="rounded-none border-[#0a0a0a] max-w-4xl"><DialogHeader><DialogTitle className="sr-only">Loading proposal</DialogTitle></DialogHeader><div className="p-8 label-eyebrow">Loading…</div></DialogContent>;

  const generateAI = async (optIdx) => {
    setBusy(`ai-${optIdx}`);
    try {
      await api.post(`/proposals-v2/${id}/generate-ai`, { option_index: optIdx });
      toast.success(`AI content generated for Option ${optIdx}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "AI failed"); }
    finally { setBusy(null); }
  };

  const saveOption = async (optKey, patch) => {
    await api.patch(`/proposals-v2/${id}`, { [optKey]: { ...prop[optKey], ...patch } });
    reload();
  };

  // Dotted-path PATCH so only the line-item override is touched (null resets
  // the option back to the template's default BOM).
  const saveOptionItems = async (optKey, items) => {
    const cleaned = cleanOverride(items);
    try {
      await api.patch(`/proposals-v2/${id}`, { [`${optKey}.items_override`]: cleaned });
      toast.success(cleaned ? "Line items updated" : "Line items reset to template defaults");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const share = async () => {
    setBusy("share");
    try {
      const r = await api.post(`/proposals-v2/${id}/share`);
      const url = `${window.location.origin}${r.data.share_path}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Public link copied to clipboard");
      reload(); onChange();
    } catch (e) { toast.error("Share failed"); }
    finally { setBusy(null); }
  };

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const r = await api.get(`/proposals-v2/${id}/pdf`, { responseType: "blob" });
      const u = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = u;
      a.download = `Proposal-${prop.customer?.name?.replace(/\s+/g, "_")}-v${prop.version}.pdf`;
      a.click();
      window.URL.revokeObjectURL(u);
    } catch (e) { toast.error("PDF failed"); }
    finally { setBusy(null); }
  };

  const dup = async () => {
    setBusy("dup");
    try {
      const r = await api.post(`/proposals-v2/${id}/duplicate`);
      toast.success(`Saved as v${r.data.version}`);
      onChange();
      onClose();
    } catch (e) { toast.error("Duplicate failed"); }
    finally { setBusy(null); }
  };

  const confirmProposal = async () => {
    // Single-option proposals: skip the picker dialog.
    if (!prop.option2) {
      if (!window.confirm(`Confirm proposal v${prop.version} as accepted by ${prop.customer?.name}? This moves the lead to "Won" and locks edits (only admin can edit a confirmed proposal).`)) return;
      setBusy("confirm");
      try {
        await api.post(`/proposals-v2/${id}/confirm`, { option_index: 1 });
        toast.success("Proposal confirmed · Lead moved to Won");
        reload(); onChange();
      } catch (e) { toast.error(e.response?.data?.detail || "Confirm failed"); }
      finally { setBusy(null); }
      return;
    }
    // Two-option proposals: open the picker dialog.
    setConfirmDlgOpen(true);
  };

  const doConfirmWithOption = async (optionIndex) => {
    setBusy("confirm");
    try {
      await api.post(`/proposals-v2/${id}/confirm`, { option_index: optionIndex });
      toast.success(`Proposal confirmed (Option ${optionIndex}) · Lead moved to Won`);
      setConfirmDlgOpen(false);
      reload(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Confirm failed"); }
    finally { setBusy(null); }
  };

  const unconfirmProposal = async () => {
    if (!window.confirm("Unconfirm this proposal? A different version can then be confirmed instead.")) return;
    setBusy("unconfirm");
    try {
      await api.post(`/proposals-v2/${id}/unconfirm`);
      toast.success("Proposal unconfirmed");
      reload(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Unconfirm failed"); }
    finally { setBusy(null); }
  };

  const deleteProposal = async () => {
    if (!window.confirm(`Delete proposal v${prop.version}? This cannot be undone.`)) return;
    setBusy("del");
    try {
      await api.delete(`/proposals-v2/${id}`);
      toast.success("Proposal deleted");
      onChange();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setBusy(null); }
  };

  const shareUrl = prop.public_token ? `${window.location.origin}/share/proposal/${prop.public_token}` : null;

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="proposal-drawer">
      <DialogHeader>
        <DialogTitle className="font-black text-2xl tracking-tight flex items-center gap-3 flex-wrap">
          {prop.customer?.name}
          <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 bg-[#F3F4F6]">v{prop.version}</span>
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${prop.status === "confirmed" ? "bg-[#002FA7] text-white" : prop.status === "sent" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#F3F4F6] text-[#4B5563]"}`}>{prop.status}</span>
          <div className="ml-auto flex flex-wrap gap-1">
            <Button onClick={dup} disabled={!!busy} size="sm" variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-8" data-testid="proposal-duplicate">
              <History className="w-3 h-3 mr-1" /> Save as new version
            </Button>
            <Button onClick={share} disabled={!!busy} size="sm" variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-8" data-testid="proposal-share">
              <LinkIcon className="w-3 h-3 mr-1" /> {prop.public_token ? "Copy link" : "Share"}
            </Button>
            {(prop.status === "sent") && (
              <Button onClick={confirmProposal} disabled={!!busy} size="sm" className="rounded-none bg-[#10B981] hover:bg-[#059669] text-white uppercase tracking-widest text-[10px] font-bold h-8" data-testid="proposal-confirm">
                ✓ Confirm
              </Button>
            )}
            {prop.status === "confirmed" && isAdmin && (
              <Button onClick={unconfirmProposal} disabled={!!busy} size="sm" variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-8" data-testid="proposal-unconfirm">
                Unconfirm
              </Button>
            )}
            <Button onClick={downloadPdf} disabled={!!busy} size="sm" className="rounded-none bg-[#0a0a0a] hover:bg-black uppercase tracking-widest text-[10px] font-bold h-8" data-testid="proposal-pdf">
              <Download className="w-3 h-3 mr-1" /> PDF
            </Button>
            {isAdmin && (
              <Button onClick={deleteProposal} disabled={!!busy} size="sm" variant="outline" className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] uppercase tracking-widest text-[10px] font-bold h-8" data-testid="proposal-delete">
                🗑 Delete
              </Button>
            )}
          </div>
        </DialogTitle>
      </DialogHeader>

      {shareUrl && (
        <div className="border border-[#10B981] bg-[#ECFDF5] p-2 text-xs flex items-center gap-2" data-testid="share-banner">
          <LinkIcon className="w-3 h-3 text-[#10B981] flex-shrink-0" />
          <a href={shareUrl} target="_blank" rel="noreferrer" className="font-mono truncate flex-1 hover:underline">{shareUrl}</a>
          <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }} className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 border border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white" data-testid="share-copy-btn">
            <Copy className="w-3 h-3" />
          </button>
        </div>
      )}

      {prop.status === "confirmed" && (
        <div className="border border-[#002FA7] bg-[#EFF6FF] p-3 text-xs flex items-start gap-2" data-testid="confirmed-banner">
          <div className="w-2 h-2 rounded-full bg-[#002FA7] mt-1 shrink-0" />
          <div className="flex-1">
            <div className="font-bold text-[#002FA7]">
              Confirmed — Option {prop.confirmed_option || 1}
              {prop.confirmed_at ? ` · ${new Date(prop.confirmed_at).toLocaleDateString()}` : ""}
              {prop.confirmed_by ? ` by ${prop.confirmed_by}` : ""}
            </div>
            <div className="text-[#4B5563] mt-0.5">
              {isAdmin ? "As admin you can still edit. Others are read-only." : "Only admin can edit a confirmed proposal. Contact your admin if changes are needed."}
              {" "}This is the version that will appear on the project page.
            </div>
          </div>
        </div>
      )}

      <ConfirmOptionDialog
        open={confirmDlgOpen}
        onOpenChange={setConfirmDlgOpen}
        prop={prop}
        busy={busy === "confirm"}
        onConfirm={doConfirmWithOption}
      />


      {/* Customer snapshot */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <Info label="Phone" value={prop.customer?.phone || "—"} />
        <Info label="Consumer No." value={prop.customer?.consumer_number || "—"} />
        <Info label="Monthly kWh" value={`${prop.customer?.monthly_consumption_kwh || 0}`} />
        <Info label="Monthly Bill" value={inr(prop.customer?.monthly_bill_inr || 0)} />
      </div>

      {/* Token Advance — edit-on-blur */}
      <div className="border border-[#E5E7EB] p-3 flex items-center gap-3 bg-[#FAFAFA]" data-testid="drawer-token-advance-row">
        <Label className="label-eyebrow !text-[10px] whitespace-nowrap">Token Advance (₹)</Label>
        <Input
          type="number"
          defaultValue={prop.token_advance_inr ?? 10000}
          key={prop.token_advance_inr ?? 0}
          onBlur={async (e) => {
            const v = Number(e.target.value) || 0;
            if (v === Number(prop.token_advance_inr ?? 10000)) return;
            try {
              await api.patch(`/proposals-v2/${id}`, { token_advance_inr: v });
              toast.success(`Token advance set to ₹${v.toLocaleString("en-IN")}`);
              reload();
            } catch { toast.error("Update failed"); }
          }}
          className="rounded-none h-9 max-w-[180px]"
          data-testid="drawer-token-advance-input"
        />
        <div className="text-[10px] text-[#9CA3AF] flex-1">Shown on the PDF (next to the UPI QR) and pre-fills the customer&apos;s UPI app on the shareable link.</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <OptionEditor
          title="Option 1"
          option={prop.option1}
          templates={templates}
          canEdit={prop.status !== "confirmed" || isAdmin}
          onSaveItems={(items) => saveOptionItems("option1", items)}
          onChangeTpl={(template_id) => saveOption("option1", { template_id, items_override: null })}
          onChangeDisc={(d) => saveOption("option1", d)}
          onGenAI={() => generateAI(1)}
          aiBusy={busy === "ai-1"}
          testidBase="drawer-opt1"
        />
        <OptionEditor
          title="Option 2"
          option={prop.option2}
          templates={templates}
          canEdit={prop.status !== "confirmed" || isAdmin}
          onSaveItems={(items) => saveOptionItems("option2", items)}
          onChangeTpl={(template_id) => saveOption("option2", { ...(prop.option2 || { discount_amount: 0, discount_type: "fixed" }), template_id, items_override: null })}
          onChangeDisc={(d) => saveOption("option2", { ...(prop.option2 || { template_id: "", discount_amount: 0, discount_type: "fixed" }), ...d })}
          onGenAI={() => generateAI(2)}
          aiBusy={busy === "ai-2"}
          testidBase="drawer-opt2"
        />
      </div>
    </DialogContent>
  );
}

function OptionEditor({ title, option, templates, onChangeTpl, onChangeDisc, onGenAI, aiBusy, testidBase, canEdit, onSaveItems }) {
  if (!option) {
    return (
      <div className="border border-dashed border-[#E5E7EB] p-4">
        <div className="label-eyebrow mb-2">{title}</div>
        <Select onValueChange={onChangeTpl}>
          <SelectTrigger className="rounded-none h-10" data-testid={`${testidBase}-template`}><SelectValue placeholder="Add Option 2 — pick a template" /></SelectTrigger>
          <SelectContent>
            {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  const tmpl = option.template;
  const roi = option.roi || {};
  const ai = option.ai_content || {};
  return (
    <div className="border border-[#0a0a0a] p-4 space-y-3" data-testid={testidBase}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{title}</div>
        <Button size="sm" onClick={onGenAI} disabled={aiBusy} className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-[10px] font-bold h-7" data-testid={`${testidBase}-ai-btn`}>
          <Sparkles className="w-3 h-3 mr-1" /> {aiBusy ? "Generating…" : "Generate AI"}
        </Button>
      </div>
      <Select value={option.template_id} onValueChange={onChangeTpl}>
        <SelectTrigger className="rounded-none h-10" data-testid={`${testidBase}-template`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {tmpl && (
        <>
          <div className="font-bold">{tmpl.name}</div>
          <div className="text-xs text-[#4B5563]">{tmpl.capacity_kw} kW · {tmpl.system_type} · {tmpl.package_type}</div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={option.discount_type} onValueChange={(v) => onChangeDisc({ discount_type: v })}>
              <SelectTrigger className="rounded-none h-9" data-testid={`${testidBase}-discount-type`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixed (₹)</SelectItem>
                <SelectItem value="percentage">Percent (%)</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number" defaultValue={option.discount_amount}
              onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== option.discount_amount) onChangeDisc({ discount_amount: v }); }}
              className="rounded-none h-9" data-testid={`${testidBase}-discount-amount`} />
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <Info label="Package" value={inr(roi.package_price_inr)} />
            <Info label="Discount" value={`− ${inr(roi.discount_inr)}`} />
            <Info label="Final" value={inr(roi.final_price_inr)} />
          </div>
          <div className="border-t border-[#F3F4F6] pt-2 grid grid-cols-2 gap-2 text-xs">
            <Info label="Annual Gen" value={`${Math.round(roi.annual_generation_kwh || 0).toLocaleString("en-IN")} kWh`} />
            <Info label="Annual Savings" value={inr(roi.annual_savings_inr)} />
            <Info label="Payback" value={`${roi.payback_years || "—"} yrs`} />
            <Info label="25-yr Savings" value={inr(roi.twentyfive_year_savings_inr)} />
          </div>
          {Object.keys(ai).length > 0 && (
            <div className="border-t border-[#F3F4F6] pt-2 space-y-2" data-testid={`${testidBase}-ai-content`}>
              {Object.entries(ai).map(([k, v]) => (
                <div key={k}>
                  <div className="label-eyebrow !text-[9px]">{k.replace(/_/g, " ")}</div>
                  <div className="text-xs text-[#0a0a0a] leading-relaxed">{v}</div>
                </div>
              ))}
            </div>
          )}
          <OptionItemsSection option={option} canEdit={canEdit} onSaveItems={onSaveItems} testidBase={testidBase} />
        </>
      )}
    </div>
  );
}

// =====================================================================
//  ROI SETTINGS DIALOG
// =====================================================================
function SettingsDialog({ open, onOpenChange }) {
  const [presets, setPresets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async (focusId) => {
    const r = await api.get("/proposal-setting-presets");
    const list = r.data || [];
    setPresets(list);
    const next = focusId
      ? list.find((p) => p.id === focusId)
      : (list.find((p) => p.id === selectedId) || list.find((p) => p.is_default) || list[0]);
    if (next) {
      setSelectedId(next.id);
      setS(next);
    } else { setSelectedId(""); setS(null); }
  };
  useEffect(() => { if (open) reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  const pickPreset = (id) => {
    const p = presets.find((x) => x.id === id);
    setSelectedId(id);
    setS(p ? { ...p } : null);
  };

  const setNum = (k) => (e) => setS({ ...s, [k]: Number(e.target.value) });
  const setBank = (k) => (e) => setS({ ...s, bank_details: { ...(s.bank_details || {}), [k]: e.target.value } });

  const uploadUpiQr = async (file) => {
    if (!file || !s) return;
    if (!file.type.startsWith("image/")) { toast.error("QR must be an image (PNG/JPG)"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("QR image too large (max 5 MB)"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/proposal-setting-presets/upload-upi-qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((cur) => ({ ...cur, bank_details: { ...(cur.bank_details || {}), upi_qr_url: r.data.path } }));
      toast.success("QR uploaded — remember to Save");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };
  const clearUpiQr = () => setS((cur) => ({ ...cur, bank_details: { ...(cur.bank_details || {}), upi_qr_url: "" } }));

  const save = async () => {
    if (!s) return;
    setBusy(true);
    try {
      const { id, created_at, updated_at, ...patch } = s; // strip read-only fields
      await api.patch(`/proposal-setting-presets/${id}`, patch);
      toast.success(`Preset "${s.name}" saved`);
      await reload(id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const createPreset = async () => {
    const name = window.prompt("Name for the new preset:", "New Preset");
    if (!name || !name.trim()) return;
    try {
      const base = s ? { ...s, name: name.trim(), is_default: false } : { name: name.trim() };
      delete base.id; delete base.created_at; delete base.updated_at;
      const r = await api.post("/proposal-setting-presets", base);
      toast.success(`Created "${r.data.name}"`);
      await reload(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setDefault = async () => {
    if (!s || s.is_default) return;
    try {
      await api.patch(`/proposal-setting-presets/${s.id}`, { is_default: true });
      toast.success(`"${s.name}" set as default`);
      await reload(s.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deletePreset = async () => {
    if (!s) return;
    if (!window.confirm(`Delete preset "${s.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/proposal-setting-presets/${s.id}`);
      toast.success("Preset deleted");
      setSelectedId("");
      setS(null);
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Warranty rows
  const addWarranty = () => setS({ ...s, warranty_items: [...(s.warranty_items || []), { item: "", warranty: "" }] });
  const setWarranty = (i, k, v) => {
    const items = [...(s.warranty_items || [])];
    items[i] = { ...items[i], [k]: v };
    setS({ ...s, warranty_items: items });
  };
  const removeWarranty = (i) => setS({ ...s, warranty_items: s.warranty_items.filter((_, idx) => idx !== i) });

  // Terms / Payment rows (strings)
  const addLine = (key) => setS({ ...s, [key]: [...(s[key] || []), ""] });
  const setLine = (key, i, v) => {
    const arr = [...(s[key] || [])];
    arr[i] = v;
    setS({ ...s, [key]: arr });
  };
  const removeLine = (key, i) => setS({ ...s, [key]: s[key].filter((_, idx) => idx !== i) });

  // Terms & Conditions — grouped subsection helpers. Each group = {heading, items[]}.
  // Backend `_normalize_terms_groups` accepts both flat and grouped shapes, but
  // we always work with the grouped shape in the UI so legacy flat data is
  // coerced by the API layer before it reaches us.
  const tncGroups = Array.isArray(s?.terms_conditions) && s.terms_conditions.length > 0
    && typeof s.terms_conditions[0] === "object" && s.terms_conditions[0] !== null
    ? s.terms_conditions
    : [{ heading: "", items: Array.isArray(s?.terms_conditions) ? s.terms_conditions : [] }];
  const setTncGroups = (groups) => setS({ ...s, terms_conditions: groups });
  const addTncGroup = () => setTncGroups([...tncGroups, { heading: "", items: [""] }]);
  const removeTncGroup = (gi) => setTncGroups(tncGroups.filter((_, i) => i !== gi));
  const setTncHeading = (gi, v) => {
    const g = [...tncGroups]; g[gi] = { ...g[gi], heading: v }; setTncGroups(g);
  };
  const addTncItem = (gi) => {
    const g = [...tncGroups];
    g[gi] = { ...g[gi], items: [...(g[gi].items || []), ""] };
    setTncGroups(g);
  };
  const setTncItem = (gi, ii, v) => {
    const g = [...tncGroups];
    const items = [...(g[gi].items || [])]; items[ii] = v;
    g[gi] = { ...g[gi], items }; setTncGroups(g);
  };
  const removeTncItem = (gi, ii) => {
    const g = [...tncGroups];
    g[gi] = { ...g[gi], items: (g[gi].items || []).filter((_, i) => i !== ii) };
    setTncGroups(g);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-[#0a0a0a] max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-black text-2xl tracking-tight">Proposal Setting Presets (Admin)</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 pt-2">
          {/* Preset list */}
          <div className="border border-[#E5E7EB] p-2 space-y-1 self-start" data-testid="preset-list">
            <div className="label-eyebrow px-2 py-1">Saved Presets</div>
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => pickPreset(p.id)}
                className={`w-full text-left px-2 py-1.5 text-xs ${selectedId === p.id ? "bg-[#0a0a0a] text-white" : "hover:bg-[#F9FAFB]"}`}
                data-testid={`preset-row-${p.id}`}
              >
                <div className="font-semibold truncate">{p.name}</div>
                {p.is_default && <div className={`text-[9px] uppercase tracking-widest ${selectedId === p.id ? "text-[#A3D9FF]" : "text-[#002FA7]"}`}>Default</div>}
              </button>
            ))}
            <Button onClick={createPreset} size="sm" variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-8 w-full mt-1" data-testid="preset-new">
              <Plus className="w-3 h-3 mr-1" /> New Preset
            </Button>
          </div>

          {/* Selected preset editor */}
          {s ? (
            <div className="space-y-5">
              {/* Header / actions */}
              <div className="border border-[#E5E7EB] p-4 flex flex-wrap items-center gap-3" data-testid="preset-header">
                <div className="flex-1 min-w-[200px]">
                  <Label className="label-eyebrow">Preset Name</Label>
                  <Input value={s.name || ""} onChange={(e) => setS({ ...s, name: e.target.value })} className="rounded-none mt-2 h-10" data-testid="preset-name" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={setDefault} disabled={s.is_default} size="sm" variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-9" data-testid="preset-set-default">
                    {s.is_default ? "Default ✓" : "Set as Default"}
                  </Button>
                  <Button onClick={deletePreset} size="sm" variant="outline" className="rounded-none border-[#DC2626] text-[#DC2626] uppercase tracking-widest text-[10px] font-bold h-9" data-testid="preset-delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Bank Details */}
              <div className="border border-[#E5E7EB] p-4 space-y-3" data-testid="preset-bank">
                <div className="label-eyebrow">Bank Details (for proposal PDF)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <TextField label="Account Holder" value={s.bank_details?.account_holder || ""} onChange={setBank("account_holder")} testid="bank-account-holder" />
                  <TextField label="Bank Name" value={s.bank_details?.bank_name || ""} onChange={setBank("bank_name")} testid="bank-bank-name" />
                  <TextField label="Account Number" value={s.bank_details?.account_number || ""} onChange={setBank("account_number")} testid="bank-account-number" />
                  <TextField label="Branch" value={s.bank_details?.branch || ""} onChange={setBank("branch")} testid="bank-branch" />
                  <TextField label="IFSC Code" value={s.bank_details?.ifsc || ""} onChange={setBank("ifsc")} testid="bank-ifsc" />
                  <TextField label="UPI ID (optional)" value={s.bank_details?.upi_id || ""} onChange={setBank("upi_id")} testid="bank-upi" placeholder="e.g. multimix@hdfc" />
                </div>

                {/* UPI QR upload — token amount is now per-proposal (see Create Proposal) */}
                <div className="border-t border-dashed border-[#E5E7EB] pt-3">
                  <Label className="label-eyebrow">UPI QR Code</Label>
                  <div className="mt-2 border border-dashed border-[#E5E7EB] p-4 flex items-center gap-4 bg-[#FAFAFA]">
                    {s.bank_details?.upi_qr_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/public/upi-qr?path=${encodeURIComponent(s.bank_details.upi_qr_url)}`}
                        alt="UPI QR"
                        className="w-32 h-32 object-contain border border-[#E5E7EB] bg-white"
                        data-testid="preset-upi-qr-preview"
                      />
                    ) : (
                      <div className="w-32 h-32 flex items-center justify-center text-[11px] text-[#9CA3AF] text-center border border-dashed border-[#E5E7EB] bg-white">
                        No QR<br/>uploaded
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-[#4B5563] leading-relaxed mb-3">
                        Upload your UPI QR (PNG / JPG · ≤ 5 MB). It will print on every proposal PDF generated with this preset and appear on the customer&apos;s shareable link.
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="border border-[#0a0a0a] text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 cursor-pointer hover:bg-[#0a0a0a] hover:text-white" data-testid="preset-upi-qr-upload-btn">
                          {s.bank_details?.upi_qr_url ? "Replace QR" : "Upload QR"}
                          <input type="file" accept="image/*" className="hidden" data-testid="preset-upi-qr-input" onChange={(e) => uploadUpiQr(e.target.files?.[0])} />
                        </label>
                        {s.bank_details?.upi_qr_url && (
                          <button type="button" onClick={clearUpiQr} className="text-[10px] uppercase tracking-widest font-bold text-[#DC2626] hover:underline" data-testid="preset-upi-qr-remove">Remove</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ROI section */}
              <div className="border border-[#E5E7EB] p-4 space-y-3">
                <div className="label-eyebrow">ROI Assumptions</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Field label="Annual Generation per kW (kWh/yr)" value={s.annual_generation_per_kw} onChange={setNum("annual_generation_per_kw")} />
                  <Field label="Default Tariff (₹/kWh)" value={s.default_tariff_inr_per_kwh} onChange={setNum("default_tariff_inr_per_kwh")} />
                  <Field label="Tariff Escalation (% / yr)" value={s.tariff_escalation_pct} onChange={setNum("tariff_escalation_pct")} />
                  <Field label="Degradation Rate (% / yr)" value={s.degradation_rate_pct} onChange={setNum("degradation_rate_pct")} />
                  <Field label="Project Life (years)" value={s.project_life_years} onChange={setNum("project_life_years")} />
                  <Field label="Sun Hours / Day" value={s.sun_hours_per_day} onChange={setNum("sun_hours_per_day")} />
                </div>
              </div>

              {/* Warranty section */}
              <ListSection title="Warranty Information" testid="settings-warranty" addLabel="Add row" onAdd={addWarranty}>
                {(s.warranty_items || []).length === 0 ? <Empty msg="No warranty rows" /> : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-[9px] uppercase tracking-widest font-bold text-[#4B5563] px-1">
                      <div className="col-span-4">Item</div>
                      <div className="col-span-7">Warranty</div>
                      <div className="col-span-1"></div>
                    </div>
                    {(s.warranty_items || []).map((w, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`warranty-row-${i}`}>
                        <Input value={w.item} onChange={(e) => setWarranty(i, "item", e.target.value)} placeholder="e.g. Solar Panels" className="rounded-none h-9 col-span-4" data-testid={`warranty-item-${i}`} />
                        <Input value={w.warranty} onChange={(e) => setWarranty(i, "warranty", e.target.value)} placeholder="e.g. 12 years product · 25 years performance" className="rounded-none h-9 col-span-7" data-testid={`warranty-text-${i}`} />
                        <button onClick={() => removeWarranty(i)} className="h-9 flex items-center justify-center text-[#DC2626] hover:bg-[#FEF2F2] border border-[#E5E7EB] col-span-1" data-testid={`warranty-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </ListSection>

              {/* Terms & Conditions — grouped subsections */}
              <div className="border border-[#E5E7EB] p-4 space-y-3" data-testid="settings-terms">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="label-eyebrow">Terms &amp; Conditions</div>
                    <div className="text-[10px] text-[#9CA3AF] mt-1">
                      Group your clauses under subsection headings (e.g. <i>Payment</i>, <i>Scope</i>, <i>Warranty Exclusions</i>). Leave a heading blank for uncategorised clauses.
                    </div>
                  </div>
                  <Button onClick={addTncGroup} size="sm" variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-8" data-testid="tnc-add-subsection">
                    <Plus className="w-3 h-3 mr-1" /> Add Subsection
                  </Button>
                </div>
                {tncGroups.map((g, gi) => (
                  <div key={gi} className="border border-[#F3F4F6] bg-[#FAFAFA] p-3 space-y-2" data-testid={`tnc-group-${gi}`}>
                    <div className="flex items-center gap-2">
                      <Input
                        value={g.heading || ""}
                        onChange={(e) => setTncHeading(gi, e.target.value)}
                        placeholder="Subsection heading (e.g. Payment Milestones) — optional"
                        className="rounded-none h-9 flex-1 font-semibold"
                        data-testid={`tnc-group-heading-${gi}`}
                      />
                      <Button
                        onClick={() => addTncItem(gi)}
                        size="sm"
                        variant="outline"
                        className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-9"
                        data-testid={`tnc-group-add-clause-${gi}`}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Clause
                      </Button>
                      {tncGroups.length > 1 && (
                        <button
                          onClick={() => removeTncGroup(gi)}
                          className="h-9 px-2 flex items-center justify-center text-[#DC2626] hover:bg-[#FEF2F2] border border-[#E5E7EB]"
                          title="Remove subsection"
                          data-testid={`tnc-group-remove-${gi}`}
                        ><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                    {(g.items || []).length === 0 ? (
                      <div className="text-center text-[11px] text-[#9CA3AF] py-3">No clauses in this subsection</div>
                    ) : (
                      <div className="space-y-2 pl-2">
                        {(g.items || []).map((line, ii) => (
                          <div key={ii} className="flex items-start gap-2" data-testid={`tnc-clause-${gi}-${ii}`}>
                            <div className="text-[10px] text-[#9CA3AF] font-mono pt-2 min-w-[16px]">{ii + 1}.</div>
                            <Textarea
                              value={line}
                              onChange={(e) => setTncItem(gi, ii, e.target.value)}
                              rows={2}
                              className="rounded-none flex-1"
                              data-testid={`tnc-clause-text-${gi}-${ii}`}
                            />
                            <button
                              onClick={() => removeTncItem(gi, ii)}
                              className="h-9 px-2 flex items-center justify-center text-[#DC2626] hover:bg-[#FEF2F2] border border-[#E5E7EB]"
                              data-testid={`tnc-clause-remove-${gi}-${ii}`}
                            ><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Payment Terms */}
              <ListSection title="Payment Terms" testid="settings-payment" addLabel="Add clause" onAdd={() => addLine("payment_terms")}>
                {(s.payment_terms || []).length === 0 ? <Empty msg="No payment clauses" /> : (
                  <div className="space-y-2">
                    {(s.payment_terms || []).map((line, i) => (
                      <div key={i} className="flex items-start gap-2" data-testid={`pay-row-${i}`}>
                        <Textarea value={line} onChange={(e) => setLine("payment_terms", i, e.target.value)} rows={2} className="rounded-none flex-1" data-testid={`pay-text-${i}`} />
                        <button onClick={() => removeLine("payment_terms", i)} className="h-9 px-2 flex items-center justify-center text-[#DC2626] hover:bg-[#FEF2F2] border border-[#E5E7EB]" data-testid={`pay-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </ListSection>

              <Button onClick={save} disabled={busy} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="settings-save">
                {busy ? "Saving…" : `Save Preset "${s.name || ""}"`}
              </Button>
            </div>
          ) : (
            <div className="border border-dashed border-[#E5E7EB] p-8 text-center text-sm text-[#9CA3AF]">
              Pick a preset on the left, or create a new one.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TextField({ label, value, onChange, testid, placeholder = "" }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input value={value} onChange={onChange} placeholder={placeholder} className="rounded-none mt-2 h-10" data-testid={testid} />
    </div>
  );
}

function ListSection({ title, testid, addLabel, onAdd, children }) {
  return (
    <div className="border border-[#E5E7EB] p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{title}</div>
        <Button onClick={onAdd} size="sm" variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-8" data-testid={`${testid}-add`}>
          <Plus className="w-3 h-3 mr-1" /> {addLabel}
        </Button>
      </div>
      {children}
    </div>
  );
}

function Empty({ msg }) {
  return <div className="text-xs text-[#9CA3AF] py-4 text-center border border-dashed border-[#E5E7EB]">{msg}</div>;
}
function Field({ label, value, onChange }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input type="number" value={value} onChange={onChange} className="rounded-none mt-2 h-10" />
    </div>
  );
}

/**
 * ConfirmOptionDialog — proper Shadcn dialog used to select which option the
 * customer has confirmed on a two-option proposal. Only opens when option2
 * exists; single-option confirmations skip straight past this UI.
 */
function ConfirmOptionDialog({ open, onOpenChange, prop, busy, onConfirm }) {
  const [selected, setSelected] = useState(1);
  useEffect(() => { if (open) setSelected(1); }, [open]);
  if (!prop) return null;
  const options = [
    { idx: 1, opt: prop.option1 },
    ...(prop.option2 ? [{ idx: 2, opt: prop.option2 }] : []),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-[#0a0a0a] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-black text-2xl tracking-tight">Confirm which option?</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-[#4B5563] mb-3">
          Select the option the customer has accepted. The other option will be discarded from the confirmed contract. Only admin can edit a confirmed proposal after this.
        </div>
        <div className="space-y-3">
          {options.map(({ idx, opt }) => {
            const t = opt?.template || {};
            const roi = opt?.roi || {};
            const isPicked = selected === idx;
            return (
              <label
                key={idx}
                className={`block border-2 p-4 cursor-pointer transition-colors ${isPicked ? "border-[#002FA7] bg-[#EFF6FF]" : "border-[#E5E7EB] hover:bg-[#F9FAFB]"}`}
                data-testid={`confirm-opt-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="confirm-option"
                    checked={isPicked}
                    onChange={() => setSelected(idx)}
                    className="mt-1 w-4 h-4 accent-[#002FA7]"
                    data-testid={`confirm-opt-${idx}-radio`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-black text-lg tracking-tight">Option {idx}</div>
                      <div className="text-lg font-mono font-black text-[#0a0a0a]">
                        {roi.final_price_inr != null ? inr(roi.final_price_inr) : "—"}
                      </div>
                    </div>
                    <div className="text-[11px] text-[#4B5563] mb-2">{t.name || "System template"}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                      <MicroStat label="Capacity" value={t.capacity_kw != null ? `${t.capacity_kw} kW` : "—"} />
                      <MicroStat label="Panels" value={t.panels_count ? `${t.panels_count} × ${t.panels_watt || 0}W` : "—"} />
                      <MicroStat label="Inverter" value={t.inverter_model || "—"} />
                      <MicroStat label="Payback" value={roi.payback_years ? `${roi.payback_years} yrs` : "—"} />
                    </div>
                    {(opt?.discount_amount || 0) > 0 && (
                      <div className="mt-2 text-[10px] text-[#059669]">
                        Discount applied: {opt.discount_type === "percentage" ? `${opt.discount_amount}%` : inr(opt.discount_amount)}
                      </div>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E7EB] mt-4">
          <Button
            variant="outline"
            className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-9 px-4"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="confirm-opt-cancel"
          >Cancel</Button>
          <Button
            className="rounded-none bg-[#10B981] hover:bg-[#059669] text-white uppercase tracking-widest text-[10px] font-bold h-9 px-4"
            onClick={() => onConfirm(selected)}
            disabled={busy}
            data-testid="confirm-opt-submit"
          >
            {busy ? "Confirming…" : `Confirm Option ${selected}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MicroStat({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest font-bold text-[#9CA3AF]">{label}</div>
      <div className="text-xs font-semibold mt-0.5 truncate" title={value}>{value}</div>
    </div>
  );
}

